// The alerts bell — in the chrome's right cluster, immediately left of the
// avatar (platform rule 2026-09-21). @wise/ui ships no bell, so it is built
// here, and it is built HONEST.
//
// It reads GET /api/alerts, which calls the SAME server function as the
// Launchpad's GET /api/internal/work-counts. The bell and "Needs you" are one
// set and cannot drift, because there is only one copy of the rules.
//
// Three rules it never breaks:
//   · a zero produces NO line;
//   · a source that could not be read is SAID, never folded into a zero and
//     never left out — "nothing is waiting" and "we could not find out" are
//     different answers;
//   · the badge counts LINES, not records: incidents and deadlines are
//     different nouns, and their sum is a number nobody can act on.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { AlertsResponse } from '../lib/types';
import { BellIcon } from './Shell';

type BellState = { status: 'loading' } | { status: 'failed' } | { status: 'ready'; data: AlertsResponse };

const at = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

export function AlertsBell({ compact }: { compact: boolean }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<BellState>({ status: 'loading' });
  const box = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api
      .alerts()
      .then((data) => setState({ status: 'ready', data }))
      .catch(() => setState({ status: 'failed' }));
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const items = state.status === 'ready' ? state.data.items : [];
  const unreadable = state.status === 'ready' ? state.data.unreadable : state.status === 'failed' ? ['the alert counts'] : [];
  const label =
    state.status === 'loading'
      ? 'Alerts — still reading the counts'
      : items.length > 0
        ? `Alerts — ${items.length} ${items.length === 1 ? 'thing' : 'things'} waiting`
        : unreadable.length > 0
          ? 'Alerts — some counts could not be read'
          : 'Alerts — nothing waiting';

  return (
    <div className="cw-bell" ref={box}>
      <button
        type="button"
        className={compact ? 'cw-bell-btn compact' : 'cw-bell-btn'}
        aria-label={label}
        aria-expanded={open}
        title={label}
        onClick={() => {
          const next = !open;
          setOpen(next);
          // Re-read on open rather than polling: a stale bell is worse than
          // one request when somebody actually looks.
          if (next) load();
        }}
      >
        <BellIcon size={16} />
        {items.length > 0 ? (
          <span className="cw-bell-n">{items.length > 99 ? '99+' : items.length}</span>
        ) : unreadable.length > 0 ? (
          <span className="cw-bell-n warn" aria-hidden="true">
            !
          </span>
        ) : null}
      </button>

      {open && (
        <div className="cw-bell-p" role="dialog" aria-label="Alerts">
          <div className="cw-bell-h">
            <span>Alerts</span>
            {state.status === 'ready' && <span className="hint">read {at(state.data.at)}</span>}
          </div>

          {state.status === 'loading' && <div className="cw-bell-s">Reading the counts…</div>}

          {items.map((i) => (
            <button
              type="button"
              key={i.key}
              className={`cw-bell-i ${i.tone ?? 'info'}`}
              onClick={() => {
                setOpen(false);
                navigate(i.path);
              }}
            >
              <span>
                <b>{i.count}</b> {i.title.charAt(0).toLowerCase() + i.title.slice(1)}
                {i.description ? <span className="d"> — {i.description}</span> : null}
              </span>
              {i.action && <span className="go">{i.action} →</span>}
            </button>
          ))}

          {unreadable.map((what) => (
            <div key={what} className="cw-bell-s">
              <b>Could not read {what}.</b> This list is incomplete — there may be work waiting that is not shown here.
            </div>
          ))}

          {state.status === 'ready' && items.length === 0 && unreadable.length === 0 && (
            <div className="cw-bell-s">
              <b>Nothing is waiting.</b> Every count this bell reads came back at zero.
            </div>
          )}

          <div className="cw-bell-f">
            The same lines appear under &ldquo;Needs you&rdquo; on the WISE Launchpad. CyberWISE sends no notifications — each line opens the rows behind it.
          </div>
        </div>
      )}
    </div>
  );
}
